import { useCallback, useEffect, useRef, useState } from "react";

const KEY_PREFIX = "wato.sidebar.collapsed.";
/** Legacy threshold (kept for callers); OWN-219: the icon rail is now the default at every width. */
export const AUTO_COLLAPSE_BELOW_PX = 1280;

export function readStoredCollapsed(userId: string): boolean | null {
  try {
    const v = window.localStorage.getItem(KEY_PREFIX + userId);
    return v === "1" ? true : v === "0" ? false : null;
  } catch {
    return null;
  }
}

function writeStoredCollapsed(userId: string, collapsed: boolean): void {
  try {
    window.localStorage.setItem(KEY_PREFIX + userId, collapsed ? "1" : "0");
  } catch {
    /* storage unavailable: the choice just is not remembered */
  }
}

/** OWN-219: a first visit starts on the icon rail; the user can expand it and that is remembered. */
function defaultCollapsed(): boolean {
  return true;
}

/** Whether the desktop sidebar is the icon rail, remembered per user; Ctrl+B (Cmd+B) toggles it. */
export function useSidebarCollapsed(
  userId: string,
  /** The page wants the icon rail unless the user chose otherwise (the inbox). */
  preferCollapsed = false,
): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(
    () => readStoredCollapsed(userId) ?? (preferCollapsed || defaultCollapsed()),
  );
  // A toggle made before the real user id is known (it is "anon" while auth loads) is the user's
  // choice: carry it over instead of letting the re-read below revert it.
  const pendingChoice = useRef<boolean | null>(null);
  const lastUserId = useRef(userId);
  useEffect(() => {
    const pending = pendingChoice.current;
    if (pending !== null && lastUserId.current === "anon" && userId !== "anon") {
      pendingChoice.current = null;
      lastUserId.current = userId;
      writeStoredCollapsed(userId, pending);
      setCollapsed(pending);
      return;
    }
    lastUserId.current = userId;
    setCollapsed(readStoredCollapsed(userId) ?? (preferCollapsed || defaultCollapsed()));
  }, [userId, preferCollapsed]);
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      writeStoredCollapsed(userId, !prev);
      if (userId === "anon") pendingChoice.current = !prev;
      return !prev;
    });
  }, [userId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "b" || !(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey)
        return;
      const target = e.target as HTMLElement | null;
      if (target?.isContentEditable) return; // Ctrl+B is "bold" in rich editors
      e.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle]);
  return [collapsed, toggle];
}
